import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {AuthenticatedBaseComponent} from '../../../authenticated-base.component';
import {Backend, PERMISSION} from '@generated-backend-types';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {ContentHeaderService} from '../../../../services/content-header.service';
import {TitleService} from '../../../../services/title.service';
import {InquiryService} from '../../../../services/api/inquiry.service';
import {NonNullableFormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {FormGroupComponent} from '../../../../components/form-inputs/form-group/form-group.component';
import {AddableDoctorService} from '../../../../services/api/patients/addable-doctor.service';
import {UserSessionService} from '../../../../services/user-session.service';
import {ParseIsoPipeModule} from 'ngx-date-fns';
import {BsModalService} from 'ngx-bootstrap/modal';
import {ToastrService} from 'ngx-toastr';
import {EMPTY, Subject, switchMap, takeUntil} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {EmailSendComponent} from '../../../../components/email-send/email-send.component';
import {
  MultiSelectWithServiceComponent
} from '../../../../components/form-inputs/multi-select-with-service/multi-select-with-service.component';
import {InquiryComponent} from './inquiry/inquiry.component';
import {FormSubmitService} from '../../../../services/form-submit.service';
import {TemplateModalService} from '../../../../services/template-modal.service';
import Patient = Backend.App.Resources.Dashboard.Patient;
import Inquiry = Backend.App.Resources.Dashboard.Inquiry.Inquiry;
import InquiryData = Backend.App.Data.Dashboard.Inquiry.InquiryData;
import RoleEnum = Backend.App.Enums.Auth.RoleEnum;
import EmailTypeEnum = Backend.App.Enums.Administration.EmailTypeEnum;
import ReducedUser = Backend.App.Resources.Dashboard.Administration.ReducedUser;
import PatientEmailData = Backend.App.Data.Dashboard.Administration.PatientEmailData;
import {INQUIRY_QUESTION_DEFAULT_VALUE} from '../../../../shared/constants';

@Component({
  selector: 'app-authenticated-patients-edit-inquiries',
  templateUrl: './inquiries.component.html',
  imports: [
    TranslatePipe,
    FormGroupComponent,
    ReactiveFormsModule,
    ParseIsoPipeModule,
    EmailSendComponent,
    MultiSelectWithServiceComponent,
    InquiryComponent,
  ],
  providers: [BsModalService, TemplateModalService],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InquiriesComponent extends AuthenticatedBaseComponent implements OnInit {
  @Input({required: true}) public patient!: Patient;
  public inquiries: Inquiry[] = [];
  public form = this.fb.group({
    doctor: this.fb.control<ReducedUser[]>([], [Validators.required]),
    question: this.fb.control(INQUIRY_QUESTION_DEFAULT_VALUE),
  });
  @ViewChild('emailBuilderModal') public emailBuilderModalTemplate?: TemplateRef<string>;
  public isWaitingForResponse = false;
  protected isBackofficeUser = false;
  protected readonly EmailTypeEnum = EmailTypeEnum;

  private readonly reload$ = new Subject<void>();

  public constructor(
    protected override readonly cdr: ChangeDetectorRef,
    protected override readonly translate: TranslateService,
    protected override readonly contentHeaderService: ContentHeaderService,
    protected override readonly titleService: TitleService,
    private readonly fb: NonNullableFormBuilder,
    private readonly inquiryService: InquiryService,
    protected readonly addableDoctorService: AddableDoctorService,
    private readonly userSessionService: UserSessionService,
    private readonly toastrService: ToastrService,
    private readonly templateModalService: TemplateModalService,
    private readonly formSubmitService: FormSubmitService,
  ) {
    super(cdr, translate, contentHeaderService, titleService);
  }

  public get selectedDoctor(): ReducedUser | undefined {
    if (this.form.controls.doctor.value.length > 0) {
      return this.form.controls.doctor.value[0];
    }

    return undefined;
  }

  public ngOnInit() {
    this.reload$.pipe(
      takeUntil(this.onDestroy$),
      switchMap(() => this.inquiryService
        .loadAllPages({'filter[patient_id]': this.patient.id, 'sort': '-created_at'}, true)
        .pipe(catchError(() => {
          this.toastrService.error(this.translate.instant('authenticated.patients.edit.inquiries.load.error'));
          return EMPTY;
        }))
      )
    ).subscribe(inquiries => {
      this.inquiries = inquiries;
      this.cdr.markForCheck();
    });

    this.loadInquiries();

    this.isBackofficeUser = this.userSessionService.hasAllRoles([RoleEnum.ADMIN]);
  }

  public save(emailData: PatientEmailData) {
    if (!this.isBackofficeUser) return;

    this.isWaitingForResponse = true;

    this.formSubmitService.submit<Inquiry, InquiryData, InquiryService, { doctor: ReducedUser[], question: string }>({
      form: this.form,
      service: this.inquiryService,
      createPermission: [PERMISSION.PATIENT_UPDATE_ALL, PERMISSION.INQUIRY_INQUIRY_CREATE_ALL],
      createSuccessMessage: this.translate.instant('authenticated.patients.edit.inquiries.create.success'),
      createErrorMessage: this.translate.instant('authenticated.patients.edit.inquiries.create.error'),
      dataTransformer: data => {
        return {
          patient_id: this.patient.id,
          doctor_id: data.doctor[0].id,
          question: data.question,
          ...emailData,
        }
      }
    }).pipe(takeUntil(this.onDestroy$)).subscribe({
      next: () => {
        this.form.reset({doctor: [], question: INQUIRY_QUESTION_DEFAULT_VALUE});
        this.onCloseEmailModal();
        this.loadInquiries();
      },
      error: () => {
        this.isWaitingForResponse = false;
        this.cdr.markForCheck();
      },
      complete: () => {
        this.isWaitingForResponse = false;
        this.cdr.markForCheck();
      }
    });
  }

  public showEmailModal() {
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();

    if (!this.form.valid || !this.isBackofficeUser) {
      return;
    }

    if (this.emailBuilderModalTemplate) {
      this.templateModalService.open(this.emailBuilderModalTemplate);
    }
  }

  public onCloseEmailModal() {
    this.templateModalService.close();
  }

  protected loadInquiries() {
    this.reload$.next();
  }
}
